import { IFilterablePaginatedSection } from "@faire/web-api/indigofair/data/IFilterablePaginatedSection";
import { Row } from "@faire/web/slate/Layout";
import React from "react";

import { Filter } from "@faire/retailer/components/Filters/Filter";
import { getSDUIFilterName } from "@faire/retailer/components/SDUI/__internal__/Filters/getSDUIFilterName";
import { SDUISectionInstanceRendererProps } from "@faire/retailer/components/SDUI/__internal__/types";
import { SDUIFilterablePaginatedSectionCategoryNavigation } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/CategoryNavigation/SDUIFilterablePaginatedSectionCategoryNavigation";
import { FilterablePaginatedSectionComponentsColumn } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Components/SDUIFilterablePaginatedSectionComponentsColumn";
import { FilterablePaginatedSectionWrapper } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionWrapper";
import { SDUIFilterStoreProvider } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/SDUIFilterStoreProvider";
import { SDUIFilterablePaginatedSectionPagination } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Pagination/SDUIFilterablePaginatedSectionPagination";
import { SDUIRetrievalQueries } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/RetrievalQueries/SDUIRetrievalQueries";
import {
  sideBarWrapper,
  styledFilter,
} from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/styles.css";
import { useFilterablePaginatedSectionQuery } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/useFilterablePaginatedSectionQuery";
import { SDUIFilterablePaginatedSectionFilterAndCatNavLoadingSkeleton } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/SDUIFilterablePaginatedSectionFilterAndCatNavLoadingSkeleton";

export const SDUIFilterablePaginatedSectionRenderer: React.FC<
  SDUISectionInstanceRendererProps
> = ({ section, ...rest }) => {
  const filterablePaginatedSection = section.filterable_paginated_section;
  if (filterablePaginatedSection) {
    return (
      <FilterablePaginatedSectionRenderer
        filterablePaginatedSection={filterablePaginatedSection}
        {...rest}
      />
    );
  }
  return null;
};

const FilterablePaginatedSectionRenderer: React.FC<
  Omit<SDUISectionInstanceRendererProps, "section"> & {
    filterablePaginatedSection: IFilterablePaginatedSection;
  }
> = ({
  filterablePaginatedSection,
  componentRenderers,
  sectionLoggingData,
}) => {
  const {
    isFilterLoading,
    isNavigationTreeLoading,
    filtersResponse,
    navigationTreeData,
    filteredComponentsData,
  } = useFilterablePaginatedSectionQuery(filterablePaginatedSection);

  const shouldShowLeftSideLoadingSkeleton =
    (isFilterLoading && filtersResponse === undefined) ||
    (isNavigationTreeLoading && navigationTreeData === undefined);
  return (
    <FilterablePaginatedSectionWrapper>
      <SDUIRetrievalQueries
        filterableComponentsResponse={filteredComponentsData}
      />
      <SDUIFilterStoreProvider
        isFiltersLoading={isFilterLoading}
        filtersResponse={filtersResponse}
      >
        <Row>
          <div className={sideBarWrapper}>
            {shouldShowLeftSideLoadingSkeleton ? (
              <SDUIFilterablePaginatedSectionFilterAndCatNavLoadingSkeleton />
            ) : (
              <>
                <SDUIFilterablePaginatedSectionCategoryNavigation
                  navigationTree={navigationTreeData}
                />
                <Filter
                  name={getSDUIFilterName(sectionLoggingData.view_display_page)}
                  resultsCount={
                    filtersResponse?.formatted_total_results_count ??
                    `${filtersResponse?.total_results_count}` ??
                    ""
                  }
                  isLoading={isFilterLoading}
                  className={styledFilter}
                  isSticky
                  headerSeparator
                />
              </>
            )}
          </div>
          <FilterablePaginatedSectionComponentsColumn
            filterablePaginatedSection={filterablePaginatedSection}
            componentRenderers={componentRenderers}
            sectionLoggingData={sectionLoggingData}
          />
        </Row>
        <SDUIFilterablePaginatedSectionPagination
          paginationData={filteredComponentsData?.pagination_data}
          sectionLoggingData={sectionLoggingData}
        />
      </SDUIFilterStoreProvider>
    </FilterablePaginatedSectionWrapper>
  );
};
